import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { fetchOfferSettings } from "../../../api/offer-settings";
import { offerSetupIssues } from "./OfferSetupStatus";

const GuideTaskRow = ({ icon: Icon, title, status, description, to, linkLabel }) => {
  const tone =
    status === "ok"
      ? "border-emerald-200 bg-emerald-50/50"
      : status === "warn"
        ? "border-amber-200 bg-amber-50/40"
        : "border-slate-200 bg-slate-50/60";

  const StatusIcon =
    status === "ok" ? CheckCircleIcon : status === "warn" ? ExclamationTriangleIcon : InformationCircleIcon;

  const statusColor =
    status === "ok" ? "text-emerald-700" : status === "warn" ? "text-amber-800" : "text-slate-600";

  return (
    <div className={`flex flex-col gap-3 rounded-2xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${tone}`}>
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-slate-700 shadow-sm">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          <div className={`mt-0.5 flex items-center gap-1.5 text-xs font-medium ${statusColor}`}>
            <StatusIcon className="h-4 w-4 shrink-0" />
            <span>{description}</span>
          </div>
        </div>
      </div>
      {to ? (
        <Link
          to={to}
          className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-500"
        >
          {linkLabel}
          <ArrowRightIcon className="h-4 w-4" />
        </Link>
      ) : null}
    </div>
  );
};

export const OfferGuideSection = () => {
  const { t } = useTranslation();
  const currentCompany = useSelector((state) => state.session.currentCompany);
  const [flags, setFlags] = useState(null);
  const [recommended, setRecommended] = useState([]);
  const [loadError, setLoadError] = useState(false);

  const offersModuleOn =
    !Array.isArray(currentCompany?.active_modules) ||
    currentCompany.active_modules.length === 0 ||
    currentCompany.active_modules.includes("offers");

  useEffect(() => {
    if (!offersModuleOn) return;
    let cancelled = false;

    fetchOfferSettings()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data !== undefined ? res.data.data : res?.data;
        setFlags(data?.flags && typeof data.flags === "object" ? data.flags : {});
        setRecommended(Array.isArray(data?.recommended) ? data.recommended : []);
        setLoadError(false);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [offersModuleOn, currentCompany?.id]);

  const issues = useMemo(
    () => offerSetupIssues({ flags, recommended, loadError }),
    [flags, recommended, loadError]
  );

  if (!offersModuleOn) return null;

  const rec = recommended.length ? recommended : [];
  const rows = rec.length
    ? rec
    : ["offer_revisions", "offer_checklist", "offer_calculation_v2", "offer_v2_overview", "offer_document_builder"];

  return (
    <section id="offers-setup-status" className="scroll-mt-24 border-b border-slate-100 py-8">
      <div className="mb-1 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
        {t("user_guide.offers.checklist.badge")}
      </div>
      <h2 className="mt-3 text-lg font-semibold text-slate-900">{t("user_guide.offers.checklist.heading")}</h2>
      <p className="mt-1 text-sm text-slate-600">{t("user_guide.offers.checklist.subtitle")}</p>

      <div className="mt-4 space-y-2">
        {loadError ? (
          <GuideTaskRow
            icon={ExclamationTriangleIcon}
            title={t("user_guide.offers.checklist.load_error.title")}
            status="warn"
            description={t("user_guide.offers.checklist.load_error.description")}
            to="/dashboard/offers/settings"
            linkLabel={t("user_guide.offers.checklist.open_settings")}
          />
        ) : (
          rows.map((key) => {
            const ok = Boolean(flags?.[key]);
            return (
              <GuideTaskRow
                key={key}
                icon={Cog6ToothIcon}
                title={t(`offers.settings.flags.${key}.title`, { defaultValue: key })}
                status={ok ? "ok" : "warn"}
                description={
                  ok
                    ? t("user_guide.offers.checklist.flag_on")
                    : t("user_guide.offers.checklist.flag_off")
                }
                to={`/dashboard/offers/settings#flag-${key}`}
                linkLabel={t("user_guide.offers.checklist.open_settings")}
              />
            );
          })
        )}
      </div>

      {!loadError && issues.length === 0 && flags ? (
        <p className="mt-3 text-sm text-emerald-700">{t("user_guide.offers.checklist.all_ok")}</p>
      ) : null}
    </section>
  );
};
